/**
 * React Query hooks for the product store.
 *
 * Provides useProducts (query) and useCheckout (mutation).
 * Follows the same pattern as @/lib/account/useAccount.ts.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { checkout, getProductBySlug, getProducts } from './storeApi'
import { storeKeys } from './storeKeys'
import type {
  CheckoutRequest,
  CheckoutResponse,
  Product,
  ProductDetail,
} from './types'

const TEN_MINUTES = 10 * 60 * 1000

/**
 * Fetch the full product catalog.
 * staleTime: 10 minutes -- prices don't change often.
 * retry: 1 -- single retry on transient failure.
 */
export function useProducts() {
  return useQuery({
    queryKey: storeKeys.products(),
    queryFn: async (): Promise<Product[]> => {
      const response = await getProducts()
      return response.products
    },
    staleTime: TEN_MINUTES,
    retry: 1,
  })
}

/**
 * Fetch a single product by slug for the /modules/:slug detail page.
 * Pass `undefined` to disable the query (e.g. when the route param is absent).
 * Doesn't retry on 404 — "not found" is a terminal state for that slug.
 */
export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: storeKeys.product(slug ?? ''),
    queryFn: async (): Promise<ProductDetail> => {
      const response = await getProductBySlug(slug as string)
      return response.product
    },
    enabled: !!slug,
    staleTime: TEN_MINUTES,
    retry: (failureCount, err) => {
      const msg = err instanceof Error ? err.message : ''
      if (msg === 'Product not found') return false
      return failureCount < 1
    },
  })
}

/**
 * Mutation hook for initiating Stripe checkout.
 * On success: redirects to the Stripe checkout URL (same tab).
 * On error: shows a toast with the error message.
 */
export function useCheckout() {
  return useMutation({
    mutationFn: (request: CheckoutRequest) => checkout(request),
    onSuccess: (data: CheckoutResponse) => {
      if (data.url) {
        window.location.href = data.url
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Checkout failed'
      toast.error(message)
    },
  })
}
